import { getPlatform } from "@/features/identity/constants/platforms";
import { MEDIA, MAX_MEDIA_BYTES } from "@/features/identity/constants/media";

export const LIMITS = { name: 150, title: 150, company: 200, bio: 500, url: 2000 };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

const clean = (errors) => Object.fromEntries(Object.entries(errors).filter(([, v]) => v));
export const hasErrors = (errors) => Object.keys(errors).length > 0;

export function checkUrl(value, domains = null, label = "") {
  const v = (value || "").trim();
  if (!v) return "Enter a link.";
  if (v.length > LIMITS.url) return "Keep links under 2,000 characters.";
  if (!SCHEME_RE.test(v)) return "Add https:// at the start.";
  let url;
  try {
    url = new URL(v);
  } catch {
    return "Enter a valid web address.";
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return "Only web links (https://) are allowed.";
  const host = url.hostname.toLowerCase();
  if (!host.includes(".")) return "Enter a valid web address.";
  if (domains && !domains.some((d) => host === d || host.endsWith(`.${d}`))) return `This doesn't look like a ${label} link.`;
  return null;
}

function httpsSuggestion(value, domains, label) {
  const v = (value || "").trim();
  if (!v || SCHEME_RE.test(v)) return null;
  const candidate = `https://${v}`;
  return checkUrl(candidate, domains, label) ? null : candidate;
}

export const checkLinkUrl = (platform, value) => {
  const p = getPlatform(platform);
  return checkUrl(value, p.domains, p.label);
};
export const linkSuggestion = (platform, value) => {
  const p = getPlatform(platform);
  return httpsSuggestion(value, p.domains, p.label);
};
export const websiteSuggestion = (value) => httpsSuggestion(value, null, "");
export const isSafeWebUrl = (value) => !checkUrl(value);

function requiredText(value, max, label) {
  const t = value.trim();
  if (!t) return `${label} is required.`;
  if (t.length < 2) return `${label} is too short.`;
  if (t.length > max) return `Keep it under ${max} characters.`;
  return null;
}

function phoneError(value) {
  const t = value.trim();
  if (!t) return null;
  if (!/^[+\d\s\-()]+$/.test(t)) return "Use digits, spaces, +, - or brackets only.";
  const digits = t.replace(/\D/g, "").length;
  return digits < 6 || digits > 20 ? "Enter a phone number with 6–20 digits." : null;
}

export const validateIdentity = (v) =>
  clean({
    full_name: requiredText(v.full_name, LIMITS.name, "Full name"),
    professional_title: requiredText(v.professional_title, LIMITS.title, "Professional title"),
    company_name: v.company_name.trim().length > LIMITS.company ? `Keep it under ${LIMITS.company} characters.` : null,
  });

export const validateContact = (v) =>
  clean({
    email: !v.email.trim() ? "Email is required." : !EMAIL_RE.test(v.email.trim()) ? "Enter a valid email address." : null,
    phone: phoneError(v.phone),
    website: v.website.trim() ? checkUrl(v.website) : null,
  });

export const validateAbout = (v) =>
  clean({ bio: v.bio.trim().length > LIMITS.bio ? `Keep your bio under ${LIMITS.bio} characters.` : null });

export function validateLinks(rows) {
  const errors = {};
  const seen = new Set();
  rows.forEach((row) => {
    const key = row.platform.toLowerCase();
    if (seen.has(key)) errors[row.key] = `You already have a ${getPlatform(row.platform).label} link.`;
    else {
      seen.add(key);
      const e = checkLinkUrl(row.platform, row.url);
      if (e) errors[row.key] = e;
    }
  });
  return errors;
}

export function validateMediaFile(kind, file) {
  const cfg = MEDIA[kind];
  if (!file) return "Choose a file.";
  if (!cfg.types.includes(file.type)) return `Please use a ${cfg.typesLabel} image.`;
  if (file.size === 0) return "This file is empty.";
  if (file.size > MAX_MEDIA_BYTES) return "Images must be 5 MB or smaller.";
  return null;
}
